import { useMemo } from 'react';
import { useFetchPickups } from '@/hooksApi';
import { PickupItem } from '@/types';
import { formatDateByDayjs } from '@/utils';

/**
 * Hook for getting pickup data
 * @param current Current page
 * @param pageSize Page size
 * @param enabled Whether to enable the query
 * @param search Optional search query string
 */
const usePickupData = (current: number, pageSize?: number, enabled = true, search?: string) => {
  const { data, isLoading, isFetching, refetch } = useFetchPickups(
    current,
    pageSize,
    enabled,
    search,
  );

  // Memoized so the mapped rows keep a STABLE reference between renders while
  // the underlying query result is unchanged. Without this, the `.map()` ran on
  // every render and handed back a brand-new array each time; the Pickups page
  // feeds this array into the rollover-toast effect's dependency list, so an
  // unstable reference made that effect fire every render and setState a fresh
  // toast object whenever a slot_response_open pickup existed → render →
  // setState → React error #185 ("Maximum update depth exceeded"), which
  // blanked the merchant's Pickups view. Keying on `data` (the React Query
  // cache object, itself stable until a real refetch) + `enabled` fixes it at
  // the source. See src/pages/pickups/useRolloverToastSync.ts for the
  // defense-in-depth equality guard on the consuming effect.
  const dataPickup = useMemo(
    () =>
      !enabled
        ? []
        : (data?.object?.items || []).map((item: PickupItem | undefined) => ({
            key: item?.uid ?? '',
            location: item?.pick_up_address?.title,
            driver: {
              phone: item?.driver?.phone,
              name: `${item?.driver?.first_name || ''} ${item?.driver?.last_name || ''}`,
              assign: !!item?.driver,
            },
            status: item?.status?.title,
            platform_code: item?.platform_code,
            pickupTime: formatDateByDayjs(item?.created_at, 'D MMMM YYYY'),
            label_dispatched_at: item?.label_dispatched_at,
            missed_orders_count: item?.missed_orders_count,
            orders_count: item?.orders_count,
            slot: item?.slot,
            slot_state: item?.slot_state,
            is_next_day: item?.is_next_day,
            slot_response_open: item?.slot_response_open,
          })),
    [data, enabled],
  );

  // totals
  const totals = data?.object.total ?? 0;

  return {
    dataPickup,
    totals,
    isLoading,
    isFetching,
    refetch,
  };
};

export default usePickupData;
